import React, { useEffect, useState } from 'react';
import { AppModal, AppSpin } from '@/components/atoms';
import { RA2NDatePicker } from '@/components/organisms';
import { useClinicContext } from '@/hooks/main/useClinicContext';
import {
  getRezeptOutcome,
  updateRezeptOutcome,
  type RezeptOutcomeData,
  type UpdateRezeptOutcomePayload,
} from '@/services/main/rezept/rezeptListService';

interface Props {
  isOpen: boolean;
  rezeptId: number | null;
  onClose: () => void;
  onUpdated: () => void;
}

// BD: Medix Hitowa_BD_Main Site_Monthly Operations - v1.0 / 転帰編集 (R2 only).
// outcome_types per BD: 4=継続 (default when NULL), 1=治癒, 2=中止, 3=転医.
type OutcomeId = 1 | 2 | 3 | 4;
const OUTCOME_OPTIONS: { id: OutcomeId; label: string; sortno: number }[] = [
  { id: 4, label: '継続', sortno: 1 },
  { id: 1, label: '治癒', sortno: 2 },
  { id: 2, label: '中止', sortno: 3 },
  { id: 3, label: '転医', sortno: 4 },
];
const OUTCOME_CHUSHI: OutcomeId = 2;

const DEFAULT_OUTCOME: OutcomeId = 4;

// Format yyyymm (eg 202604) → 元号YY年MM月 for display (item §1 対象年月).
// Reiwa starts 2019 → year - 2018. Fallback to "YYYY年MM月" if unknown era.
const formatWarekiMonth = (yyyymm: number): string => {
  if (!yyyymm) return '';
  const y = Math.floor(yyyymm / 100);
  const m = yyyymm % 100;
  const reiwaYear = y - 2018;
  if (reiwaYear > 0)
    return `令和${String(reiwaYear).padStart(2, '0')}年${String(m).padStart(2, '0')}月`;
  return `${y}年${String(m).padStart(2, '0')}月`;
};

// BD §4 validation: wrong format → auto-clear. Pattern accept:
//   - empty string (not entered yet)
//   - 元号YY年MM月DD日 (wareki)
//   - YYYY-MM-DD (Y-m-d, when loaded from DB)
const DAY_PATTERN = /^(明治|大正|昭和|平成|令和)\d{1,2}年\d{1,2}月\d{1,2}日$|^\d{4}-\d{2}-\d{2}$/u;
const acceptDay = (v: string): string => (v === '' || DAY_PATTERN.test(v) ? v : '');

const OutcomeEditModal: React.FC<Props> = ({ isOpen, rezeptId, onClose, onUpdated }) => {
  const { tenant, clinicNumber } = useClinicContext();

  const [data, setData] = useState<RezeptOutcomeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [outcomeId, setOutcomeId] = useState<OutcomeId>(DEFAULT_OUTCOME);
  const [stopFrom, setStopFrom] = useState<string>('');
  const [stopTo, setStopTo] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  // Field-level errors per BD §7 — display under each corresponding field.
  const [fieldErrors, setFieldErrors] = useState<{
    stop_term_from?: string;
    stop_term_to?: string;
  }>({});

  useEffect(() => {
    if (!isOpen || rezeptId == null) return;
    setLoading(true);
    setErrorMsg(null);
    getRezeptOutcome(tenant, clinicNumber, rezeptId)
      .then(d => {
        setData(d);
        // HIT-4272: r_rezepts.outcome_type_id defaults to 0 in DB (not NULL), so
        // `?? DEFAULT_OUTCOME` would not trigger. Fall back to 継続 whenever the
        // stored value is not one of the 4 valid options.
        const stored = d.outcome_type_id;
        const isValid = OUTCOME_OPTIONS.some(o => o.id === stored);
        setOutcomeId((isValid ? stored : DEFAULT_OUTCOME) as OutcomeId);
        setStopFrom(d.stop_term_from ?? '');
        setStopTo(d.stop_term_to ?? '');
        setComment(d.comment ?? '');
      })
      .catch(err => {
        const beMsg = (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message;
        setErrorMsg(beMsg || '対象のレセプトが見つかりません。');
      })
      .finally(() => setLoading(false));
  }, [isOpen, rezeptId, tenant, clinicNumber]);

  // When changing to an outcome other than 中止 → clear stop_term per BD + clear field errors.
  useEffect(() => {
    if (outcomeId !== OUTCOME_CHUSHI) {
      setStopFrom('');
      setStopTo('');
      setFieldErrors({});
      setErrorMsg(null);
    }
  }, [outcomeId]);

  const showValidationFailure = (errors: { stop_term_from?: string; stop_term_to?: string }) => {
    // BD §7: top of popup = generic msg, below each field = specific msg.
    // DO NOT fire a toast — the popup already has a message at the top, avoiding duplicates.
    setErrorMsg('更新ができませんでした。');
    setFieldErrors(errors);
  };

  const handleUpdate = async () => {
    if (!data || rezeptId == null) return;
    setErrorMsg(null);
    setFieldErrors({});

    // FE validate per BD §4 — only check when outcome=中止 (2).
    if (outcomeId === OUTCOME_CHUSHI) {
      const errs: { stop_term_from?: string; stop_term_to?: string } = {};
      if (!stopFrom) errs.stop_term_from = '中止開始日は必須です。';
      if (!stopTo) errs.stop_term_to = '中止終了日は必須です。';
      if (Object.keys(errs).length) {
        showValidationFailure(errs);
        return;
      }
    }

    const payload: UpdateRezeptOutcomePayload = {
      outcome_type_id: outcomeId,
      stop_term_from: outcomeId === OUTCOME_CHUSHI ? stopFrom || null : null,
      stop_term_to: outcomeId === OUTCOME_CHUSHI ? stopTo || null : null,
      comment: comment || null,
    };

    setSubmitting(true);
    try {
      await updateRezeptOutcome(tenant, clinicNumber, rezeptId, payload);
      // Parent (RezeptListPage.handleTenkiSuccess) handles toast + refetch + close.
      onUpdated();
    } catch (err) {
      const beErrors = (err as { response?: { data?: { errors?: Record<string, string[]> } } })
        ?.response?.data?.errors;
      const fieldErrs: { stop_term_from?: string; stop_term_to?: string } = {};
      if (beErrors?.stop_term_from?.[0]) fieldErrs.stop_term_from = beErrors.stop_term_from[0];
      if (beErrors?.stop_term_to?.[0]) fieldErrs.stop_term_to = beErrors.stop_term_to[0];
      showValidationFailure(fieldErrs);
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;
  const isChushi = outcomeId === OUTCOME_CHUSHI;

  return (
    <AppModal
      isOpen={isOpen}
      onClose={onClose}
      title="転帰編集"
      maxWidth={520}
      onOk={handleUpdate}
      okText="更新"
      okButtonProps={{ loading: submitting, icon: undefined }}
    >
      <AppSpin spinning={loading || submitting}>
        <div className="space-y-4 py-2">
          {errorMsg && (
            <div className="px-3 py-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
              {errorMsg}
            </div>
          )}

          {/* BD §1 + §2 — line 1: treatment_yearmonth, line 2: serial_number ： patient_name */}
          <div className="space-y-1 text-base font-medium text-gray-900">
            <div>{formatWarekiMonth(data?.treatement_yearmonth ?? 0)}</div>
            <div>
              {data?.serial_number ?? ''}
              {' ： '}
              {data?.patient_name ?? ''}
            </div>
          </div>

          {/* BD §3 — Radio 転帰, label + 4 options same row */}
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-700 min-w-[80px]">転帰</span>
            <div className="flex flex-wrap items-center gap-4">
              {OUTCOME_OPTIONS.map(opt => (
                <label key={opt.id} className="flex items-center gap-1 cursor-pointer">
                  <input
                    type="radio"
                    name="outcome_type_id"
                    checked={outcomeId === opt.id}
                    onChange={() => setOutcomeId(opt.id)}
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>

          {/* BD §4 — 中止期間 (datepicker From + To, only enabled when 中止)
              + field-level errors hiển thị ngay dưới mỗi datepicker per BD §7 */}
          <div className="flex items-start gap-4 text-sm">
            <span className="text-gray-700 min-w-[80px] pt-1">中止期間</span>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <div className="flex flex-col">
                  <RA2NDatePicker
                    value={stopFrom || undefined}
                    onChange={v => setStopFrom(acceptDay(v || ''))}
                    placeholder="元号00年00月00日"
                    disabled={!isChushi}
                  />
                  {fieldErrors.stop_term_from && (
                    <span className="text-xs text-red-600 mt-1">{fieldErrors.stop_term_from}</span>
                  )}
                </div>
                <span>〜</span>
                <div className="flex flex-col">
                  <RA2NDatePicker
                    value={stopTo || undefined}
                    onChange={v => setStopTo(acceptDay(v || ''))}
                    placeholder="元号00年00月00日"
                    disabled={!isChushi}
                  />
                  {fieldErrors.stop_term_to && (
                    <span className="text-xs text-red-600 mt-1">{fieldErrors.stop_term_to}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* BD §5 — コメント, max 128, label same row */}
          <label className="flex items-start gap-4 text-sm">
            <span className="text-gray-700 min-w-[80px] pt-1">コメント</span>
            <textarea
              className="flex-1 border border-gray-300 rounded px-2 py-1 text-sm"
              rows={3}
              maxLength={128}
              value={comment}
              onChange={e => setComment(e.target.value)}
            />
          </label>
        </div>
      </AppSpin>
    </AppModal>
  );
};

export default OutcomeEditModal;
